// 入社・契約解除フォーム（本城さん要望 2026-08-24/25、回答 2026-08-26 → docs/36_entry-form-draft.md）
//
// 「メアド収集フォームと同じように、入力・送信するとマスタへ反映される入社フォーム」が要望。
// **送信するのは HSD様（徳永さん）**（本城さん・徳永さん 2026-08-26 回答：単価等も含めHSD側で管理）。
// ログイン済みのアプリ内フォームとして提供する（公開フォームにはしない。単価・控除を入力するため）。
//
// 反映先:
//   入社届   → /api/master/add（マスターシートに1行追加・管理番号はサーバ採番）
//              AT-STYLE様所属 → マスタ「AT-STYLE」列に ✓ ＋ グループ集計設定（GroupRule.memberNames）にも追加
//   契約解除届 → /api/master/retire（行は消さず契約終了日を入れる論理削除。未来日は退職予定）
// どちらも既存の書き込み経路をそのまま使う（新しい書き込み経路は作らない）。
//
// 毎月の控除（ガソリンカード利用額など）はこのフォームの対象外。貸与の有無だけをここで登録し、
// 毎月の金額は明細発行後に「月別控除入力」シートへ入力する運用（本城さん 2026-08-26 回答）。
//
// メアド確認（本城さん要望 2026-08-28「アドレス送信と入社・契約解除を一緒に管理したい」）:
//   マスタに行が出来た直後に、既存のメアド収集の入口（/api/register/submit）をそのまま呼ぶ。
//   本人が確認メールのリンクを押すと、マスタの「認証済メールアドレス」に本人確認の証跡が入る。
//   ※ 確認メールは「マスタに在籍行があり、氏名が一意に決まる」ことが前提。追加直後なので通常は一意。

import React, { useEffect, useMemo, useState } from "react";
import { Ic } from "../components/Icons";
import { useStore } from "../store";
import { addDriverToSheet, retireDriverInSheet } from "../lib/masterApi";
import { registerSubmit } from "../lib/registerApi";
import { EntryRequestsPanel } from "./EntryRequests";
import { fetchEntryRequests } from "../lib/entryApi";
import { isRetiredDriver } from "../lib/driverStatus";
import { normalizeName } from "../lib/normalize";
import type { Driver, GroupRule } from "../types";
import { normalizeCompanyName } from "../lib/calc/groupInvoice";

type Tab = "join" | "leave" | "requests";

type JoinForm = {
  name: string;
  org: string;
  branch: string;
  email: string;
  contractFrom: string;
  unitTakkyu: string;
  unitNekopos: string;
  unitDm: string;
  monthlyKeitai: string;
  monthlySharyo: string;
  monthlyAppli: string;
  shidoCount: string;
  hasGasCard: boolean;
  contractDocOk: boolean;
  invoiceNo: string;
  notes: string;
  atStyle: boolean;
  // 登録と同時に本人へメアド確認メールを送る（本城さん要望 2026-08-28。既定オン）
  sendConfirm: boolean;
};

const EMPTY_JOIN: JoinForm = {
  name: "",
  org: "個人",
  branch: "",
  email: "",
  contractFrom: "",
  unitTakkyu: "",
  unitNekopos: "",
  unitDm: "",
  monthlyKeitai: "",
  monthlySharyo: "",
  monthlyAppli: "",
  shidoCount: "",
  hasGasCard: false,
  contractDocOk: false,
  invoiceNo: "",
  notes: "",
  atStyle: false,
  sendConfirm: true,
};

type LogEntry = { at: string; kind: "join" | "leave"; text: string };

const numOrNull = (v: string): number | null => {
  const t = v.trim();
  if (t === "") return null;
  const n = Number(t.replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
};

const todayIso = (): string => {
  const d = new Date();
  const jst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
  return jst.toISOString().slice(0, 10);
};

// AT-STYLE様のグループ集計ルール（設定画面で編集されるもの）。無ければ null。
// 判定は設定画面（GroupMasterMismatch）と同じ normalizeCompanyName に揃える
// （全角「ＡＴ－ＳＴＹＬＥ」や「㈱」表記でも同じルールを指すようにするため）。
function findAtStyleRule(rules: GroupRule[]): GroupRule | null {
  return rules.find((r) => normalizeCompanyName(r.groupName).includes("AT-STYLE")) ?? null;
}

export function EntryFormScreen({ onNav }: { onNav: (s: string) => void }) {
  const { state, dispatch, refreshMaster } = useStore();
  const sheetMode = !!state.sheetMaster;
  const [tab, setTab] = useState<Tab>("join");
  const [log, setLog] = useState<LogEntry[]>([]);
  // 携帯の公開フォームから届いた申請の未処理件数（タブのバッジ用）
  const [pendingCount, setPendingCount] = useState(0);
  // 画面を開いた時点で件数を取りに行く。タブを開くまでバッジが 0 のままだと、
  // 携帯から届いた届出に誰も気づけない（バッジが唯一の気づき方のため）
  useEffect(() => {
    let alive = true;
    void fetchEntryRequests().then((r) => {
      if (alive && r.ok) setPendingCount((r.requests || []).filter((x) => x.status === "pending").length);
    });
    return () => {
      alive = false;
    };
  }, []);
  const pushLog = (kind: LogEntry["kind"], text: string) =>
    setLog((l) => [{ at: new Date().toLocaleTimeString("ja-JP"), kind, text }, ...l]);

  return (
    <div className="content col gap-16">
      <div className="row" style={{ alignItems: "flex-end", gap: 12 }}>
        <div>
          <div className="page-title">入社・契約解除フォーム</div>
          <div className="tiny muted">
            HSD様（徳永さん）が入力・送信します。送信するとマスターシートに反映され、マスター編集画面で確認できます。
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button className="btn ghost" onClick={() => onNav("master")}>
          <Ic.users size={14} /> マスター編集を開く
        </button>
      </div>

      {!sheetMode && (
        <div className="toast warn">
          <Ic.alert size={14} /> マスターシート連携が無効な環境（ローカル）では送信できません。本番環境でお使いください。
        </div>
      )}

      <div className="status-tabs">
        <button className={"st-tab" + (tab === "join" ? " active" : "")} onClick={() => setTab("join")}>
          <Ic.plus size={13} /> 入社届
        </button>
        <button className={"st-tab" + (tab === "leave" ? " active" : "")} onClick={() => setTab("leave")}>
          <Ic.logOut size={13} /> 契約解除届
        </button>
        <button className={"st-tab" + (tab === "requests" ? " active" : "")} onClick={() => setTab("requests")}>
          <Ic.mail size={13} /> 携帯からの登録
          {pendingCount > 0 && <span className="chip amber" style={{ marginLeft: 6 }}>{pendingCount}</span>}
        </button>
      </div>

      {tab === "join" ? (
        <JoinPanel
          disabled={!sheetMode}
          drivers={state.drivers}
          atStyleRule={findAtStyleRule(state.groupRules)}
          onSubmitted={async (r, form) => {
            // AT-STYLE様所属 → グループ集計設定にも名前を足す（マスタに列が無いため。docs/36 未決事項②）
            const rule = findAtStyleRule(state.groupRules);
            let groupNote = "";
            if (form.atStyle && rule) {
              const already = rule.memberNames.some((n) => normalizeName(n) === normalizeName(form.name));
              if (!already) {
                // 1名追加は reducer 側で「現在の」設定に足す（rules 配列を丸ごと差し替えると、
                // 別端末で設定画面が開いたままのとき、その画面の保存で追加した人が静かに消える）
                dispatch({ type: "ADD_GROUP_MEMBER", ruleId: rule.id, name: form.name.trim() });
                groupNote = "／AT-STYLE様のグループ集計にも追加";
              }
            } else if (form.atStyle && !rule) {
              groupNote = "／⚠️ AT-STYLE様のグループ設定が見つからず、集計への追加は未実施";
            }
            await refreshMaster();

            // 登録と同時に本人へメアド確認メールを送る（本城さん要望 2026-08-28）。
            // 既存のメアド収集の入口をそのまま使う（マスタに行が出来た直後なので氏名は一意に決まる）。
            let mailNote = "";
            const email = form.email.trim();
            if (form.sendConfirm && email) {
              const rr = await registerSubmit({ name: form.name.trim(), email, branch: form.branch.trim() || undefined });
              if (rr.ok && rr.result === "sent") {
                mailNote = `／確認メールを ${email} へ送信`;
              } else if (rr.ok && rr.result === "needsReview") {
                mailNote =
                  rr.reason === "candidate"
                    ? "／⚠️ 同姓同名で本人を特定できず、確認メールは未送信（メアド収集画面の要確認から処理してください）"
                    : "／⚠️ マスタ照合で本人を特定できず、確認メールは未送信（シート反映待ちの可能性。収集画面から再送できます）";
              } else {
                mailNote = `／⚠️ 確認メールの送信に失敗（${rr.error || "原因不明"}）。メアド収集画面から再送できます`;
              }
            } else if (form.sendConfirm && !email) {
              mailNote = "／メールアドレス未入力のため確認メールは送っていません";
            }

            pushLog("join", `${form.name}（管理番号 ${r.empId}）をマスタに追加${groupNote}${mailNote}`);
          }}
        />
      ) : tab === "requests" ? (
        <EntryRequestsPanel onCount={setPendingCount} />
      ) : (
        <LeavePanel
          disabled={!sheetMode}
          drivers={state.drivers}
          onSubmitted={async (d, date) => {
            await refreshMaster();
            const future = date > todayIso();
            pushLog("leave", `${d.name}（${d.id}）の契約終了日を ${date} に設定${future ? "（退職予定。当日までは支払対象）" : "（退職）"}`);
          }}
        />
      )}

      {log.length > 0 && (
        <div className="card" style={{ padding: 14 }}>
          <div className="card-title" style={{ marginBottom: 8 }}>このセッションで送信した内容</div>
          <div className="col gap-4">
            {log.map((l, i) => (
              <div key={i} className="tiny row gap-8" style={{ alignItems: "center" }}>
                <span className="muted num">{l.at}</span>
                <span className={"chip " + (l.kind === "join" ? "green" : "blue")}>{l.kind === "join" ? "入社" : "契約解除"}</span>
                <span>{l.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ===== 入社届 =====

function JoinPanel({
  disabled,
  drivers,
  atStyleRule,
  onSubmitted,
}: {
  disabled: boolean;
  drivers: Driver[];
  atStyleRule: GroupRule | null;
  onSubmitted: (r: { empId?: string }, form: JoinForm) => Promise<void>;
}) {
  const [form, setForm] = useState<JoinForm>(EMPTY_JOIN);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  // サーバが書けなかった項目の注意（例: シートに「AT-STYLE」列がまだ無い）
  const [warnings, setWarnings] = useState<string[]>([]);

  const set = <K extends keyof JoinForm>(k: K, v: JoinForm[K]) => setForm((f) => ({ ...f, [k]: v }));

  // 送信前チェック（サーバでも同じ検証をするが、画面で先に気づけるように）。
  // blocking=true は送信を止めるもの、false は「このまま追加できるが知っておいてほしい」注意。
  const problems = useMemo(() => {
    const p: { msg: string; blocking: boolean }[] = [];
    const warn = (msg: string) => p.push({ msg, blocking: false });
    const stop = (msg: string) => p.push({ msg, blocking: true });
    const name = form.name.trim();
    if (!name) stop("氏名は必須です");
    if (name && drivers.some((d) => !isRetiredDriver(d) && normalizeName(d.name) === normalizeName(name)))
      stop(`「${name}」は在籍中で既に登録されています（重複追加になります）`);
    if (form.invoiceNo && !/^T\d{13}$/.test(form.invoiceNo.trim())) stop("登録番号は T+13桁 で入力してください");
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) stop("メールアドレスの形式が正しくありません");
    for (const [k, label] of [
      ["unitTakkyu", "宅急便単価"],
      ["unitNekopos", "ネコポス単価"],
      ["unitDm", "DM便単価"],
      ["monthlyKeitai", "携帯リース"],
      ["monthlySharyo", "車両リース"],
      ["monthlyAppli", "アプリ"],
      ["shidoCount", "指導料件数"],
    ] as const) {
      const v = form[k];
      if (v.trim() !== "" && numOrNull(v) == null) stop(`${label}は数値で入力してください`);
    }
    if (!form.unitTakkyu.trim() && !form.unitNekopos.trim()) warn("単価が未入力です（追加はできますが、計算時に「単価未設定」の警告が出ます）");
    // 契約開始日が空だと月途中入社でも在籍日数が月初からになり、携帯・車両・アプリの
    // 月額控除が日割りされず満額引かれる（prorate.ts）。控除が入っている人は特に危ないので止める。
    if (!form.contractFrom) {
      const hasMonthly = [form.monthlyKeitai, form.monthlySharyo, form.monthlyAppli].some((v) => numOrNull(v) != null);
      if (hasMonthly) {
        stop("契約開始日を入力してください（未入力だと月途中入社でも月額控除が日割りされず、満額引かれます）");
      } else {
        warn("契約開始日が未入力です（追加はできますが、後から月額控除を入れる場合は日割りされません）");
      }
    }
    return p;
  }, [form, drivers]);
  const blocking = problems.filter((m) => m.blocking);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await addDriverToSheet({
        name: form.name.trim(),
        org: form.org.trim() || "個人",
        branch: form.branch.trim(),
        email: form.email.trim() || undefined,
        contractFrom: form.contractFrom || undefined,
        unitTakkyu: numOrNull(form.unitTakkyu),
        unitNekopos: numOrNull(form.unitNekopos),
        unitDm: numOrNull(form.unitDm),
        invoiceNo: form.invoiceNo.trim() || null,
        monthlyKeitai: numOrNull(form.monthlyKeitai),
        monthlySharyo: numOrNull(form.monthlySharyo),
        hasGasCard: form.hasGasCard,
        monthlyAppli: numOrNull(form.monthlyAppli),
        shidoCount: numOrNull(form.shidoCount),
        contractDocOk: form.contractDocOk,
        notes: form.notes.trim() || undefined,
        atStyle: form.atStyle,
      });
      if (!r.ok) {
        setError(r.error || "マスターシートへの追加に失敗しました");
        setConfirming(false);
        return;
      }
      await onSubmitted(r, form);
      setWarnings(r.warnings ?? []);
      setDone(`${form.name.trim()} さんをマスタに追加しました（管理番号 ${r.empId}）`);
      setForm(EMPTY_JOIN);
      setConfirming(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="col gap-16">
        {done && (
          <div className="toast ok">
            <Ic.checkCircle size={14} /> {done}
          </div>
        )}
        {error && (
          <div className="toast warn">
            <Ic.alert size={14} /> {error}
          </div>
        )}
        {warnings.map((w, i) => (
          <div key={i} className="toast warn">
            <Ic.alert size={14} /> {w}
          </div>
        ))}

        <Section title="基本情報">
          <Row>
            <Field label="氏名" required>
              <input className="input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="例: 佐藤　義継（マスタの表記に合わせる）" />
            </Field>
            <Field label="所属">
              <input className="input" value={form.org} onChange={(e) => set("org", e.target.value)} placeholder="個人 / 法人名" />
            </Field>
            <Field label="支店">
              <input className="input" value={form.branch} onChange={(e) => set("branch", e.target.value)} placeholder="例: 佐川岩国 / ヤマト広島ベース" />
            </Field>
          </Row>
          <Row>
            <Field label="メールアドレス（承認通知先）">
              <input className="input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="例: taro@example.com" />
              <Check
                label="登録と同時に本人へ確認メールを送る"
                checked={form.sendConfirm}
                onChange={(v) => set("sendConfirm", v)}
                hint={
                  form.email.trim()
                    ? "本人がメールのリンクを押すと、マスタの「認証済メールアドレス」に本人確認の記録が入ります"
                    : "メールアドレスを入力すると送れます"
                }
              />
            </Field>
            <Field label="契約開始日">
              <input className="input" type="date" value={form.contractFrom} onChange={(e) => set("contractFrom", e.target.value)} />
            </Field>
            <Field label="登録番号（インボイス T+13桁）">
              <input className="input" value={form.invoiceNo} onChange={(e) => set("invoiceNo", e.target.value)} placeholder="T1234567890123（未登録なら空欄）" />
            </Field>
          </Row>
        </Section>

        <Section title="単価（円/個）">
          <Row>
            <Field label="宅急便">
              <input className="input" type="number" value={form.unitTakkyu} onChange={(e) => set("unitTakkyu", e.target.value)} />
            </Field>
            <Field label="ネコポス">
              <input className="input" type="number" value={form.unitNekopos} onChange={(e) => set("unitNekopos", e.target.value)} />
            </Field>
            <Field label="DM便（通常は空欄）">
              <input className="input" type="number" value={form.unitDm} onChange={(e) => set("unitDm", e.target.value)} />
            </Field>
          </Row>
        </Section>

        <Section title="毎月の控除（月額・任意）">
          <Row>
            <Field label="携帯リース">
              <input className="input" type="number" value={form.monthlyKeitai} onChange={(e) => set("monthlyKeitai", e.target.value)} />
            </Field>
            <Field label="車両リース">
              <input className="input" type="number" value={form.monthlySharyo} onChange={(e) => set("monthlySharyo", e.target.value)} />
            </Field>
            <Field label="アプリ">
              <input className="input" type="number" value={form.monthlyAppli} onChange={(e) => set("monthlyAppli", e.target.value)} />
            </Field>
            <Field label="指導料（件数）">
              <input className="input" type="number" value={form.shidoCount} onChange={(e) => set("shidoCount", e.target.value)} />
            </Field>
          </Row>
          <Row>
            <Check label="ガソリンカードを貸与する" checked={form.hasGasCard} onChange={(v) => set("hasGasCard", v)} hint="貸与ありの方のみ、毎月の利用額は明細発行後に「月別控除入力」シートへ入力します（このフォームでは金額を入れません）" />
            <Check label="契約書類 受領済み" checked={form.contractDocOk} onChange={(v) => set("contractDocOk", v)} />
          </Row>
        </Section>

        <Section title="グループ集計">
          <Row>
            <Check
              label="株式会社AT-STYLE様 所属"
              checked={form.atStyle}
              onChange={(v) => set("atStyle", v)}
              hint={
                atStyleRule
                  ? `マスターシートの「AT-STYLE」列に記録し、設定画面のグループ集計（現在 ${atStyleRule.memberNames.length} 名）にも追加します`
                  : "⚠️ 設定画面に AT-STYLE 様のグループ集計が無いため、マスタ列への記録のみ行います（集計には追加されません）"
              }
            />
          </Row>
        </Section>

        <Section title="備考">
          <Row>
            <Field label="備考（マスタの備考列に入ります）">
              <input className="input" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
            </Field>
          </Row>
        </Section>

        {problems.length > 0 && (
          <div className="col gap-4">
            {problems.map((m, i) => (
              <div key={i} className="tiny" style={{ color: m.blocking ? "var(--red)" : "var(--amber, #b7791f)" }}>
                ・{m.msg}
              </div>
            ))}
          </div>
        )}

        {!confirming ? (
          <div className="row gap-8">
            <button className="btn primary" disabled={disabled || blocking.length > 0} onClick={() => { setDone(null); setConfirming(true); }}>
              <Ic.check size={14} /> 内容を確認する
            </button>
            <button className="btn ghost" onClick={() => { setForm(EMPTY_JOIN); setDone(null); setError(null); setWarnings([]); }}>
              クリア
            </button>
          </div>
        ) : (
          <div className="card" style={{ padding: 14, background: "var(--surface-2)" }}>
            <div className="card-title" style={{ marginBottom: 8 }}>この内容でマスターシートに追加します</div>
            <div className="tiny col gap-2">
              <div>氏名: <b>{form.name.trim()}</b>　所属: {form.org.trim() || "個人"}　支店: {form.branch.trim() || "—"}</div>
              <div>
                メール: {form.email.trim() || "—"}　契約開始日: {form.contractFrom || "—"}　登録番号: {form.invoiceNo.trim() || "未登録"}
              </div>
              <div>
                確認メール:{" "}
                {form.sendConfirm && form.email.trim()
                  ? `送る（${form.email.trim()} 宛。本人がリンクを押すと認証済みになります）`
                  : form.sendConfirm
                    ? "送らない（メールアドレス未入力）"
                    : "送らない"}
              </div>
              <div>
                単価 宅急便 {form.unitTakkyu || "—"} / ネコポス {form.unitNekopos || "—"} / DM {form.unitDm || "—"}
                控除 携帯 {form.monthlyKeitai || "—"} / 車両 {form.monthlySharyo || "—"} / アプリ {form.monthlyAppli || "—"} / 指導料 {form.shidoCount || "—"}件
              </div>
              <div>
                ガソリンカード: {form.hasGasCard ? "貸与" : "—"}　契約書類: {form.contractDocOk ? "受領済み" : "—"}　AT-STYLE様所属: {form.atStyle ? "はい" : "—"}
              </div>
              {form.notes.trim() && <div>備考: {form.notes.trim()}</div>}
            </div>
            <div className="row gap-8" style={{ marginTop: 12 }}>
              <button className="btn primary" disabled={busy} onClick={submit}>
                {busy ? "シートに書き込み中..." : "送信してマスタに追加する"}
              </button>
              <button className="btn ghost" disabled={busy} onClick={() => setConfirming(false)}>
                戻って直す
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ===== 契約解除届 =====

function LeavePanel({
  disabled,
  drivers,
  onSubmitted,
}: {
  disabled: boolean;
  drivers: Driver[];
  onSubmitted: (d: Driver, date: string) => Promise<void>;
}) {
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<Driver | null>(null);
  const [date, setDate] = useState<string>(todayIso());
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  // 対象は在籍者のみ（退職者・既に契約終了日が入っている人は候補に出さない）
  const candidates = useMemo(() => {
    const active = drivers.filter((d) => !isRetiredDriver(d) && !d.contractTo);
    const t = q.trim();
    if (!t) return active;
    return active.filter((d) => d.name.includes(t) || d.id.includes(t) || (d.branch || "").includes(t));
  }, [drivers, q]);

  const future = !!date && date > todayIso();

  const submit = async () => {
    if (!picked) return;
    setBusy(true);
    setError(null);
    try {
      const r = await retireDriverInSheet(picked.id, date);
      if (!r.ok) {
        setError(r.error || "契約終了日の書き込みに失敗しました");
        setConfirming(false);
        return;
      }
      await onSubmitted(picked, date);
      setDone(`${picked.name} さんの契約終了日を ${date} に設定しました${future ? "（退職予定。当日までは支払対象のままです）" : ""}`);
      setPicked(null);
      setQ("");
      setDate(todayIso());
      setConfirming(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="col gap-16">
        {done && (
          <div className="toast ok">
            <Ic.checkCircle size={14} /> {done}
          </div>
        )}
        {error && (
          <div className="toast warn">
            <Ic.alert size={14} /> {error}
          </div>
        )}

        <Section title="対象のドライバー（在籍者から選ぶ）">
          <div className="search-wrap" style={{ maxWidth: 360 }}>
            <Ic.search size={14} />
            <input className="input search" placeholder="氏名 / 管理番号 / 支店で検索" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="tbl-scroll" style={{ maxHeight: 260 }}>
            <table className="tbl">
              <tbody>
                {candidates.slice(0, 40).map((d) => (
                  <tr key={d.id} style={picked?.id === d.id ? { background: "var(--surface-3)" } : undefined}>
                    <td className="mono muted" style={{ width: 90 }}>{d.id}</td>
                    <td style={{ fontWeight: 700 }}>{d.name}</td>
                    <td className="tiny muted">{d.org}</td>
                    <td className="tiny muted">{d.branch || "—"}</td>
                    <td style={{ width: 80 }}>
                      <button className={"btn sm " + (picked?.id === d.id ? "primary" : "")} onClick={() => { setPicked(d); setDone(null); }}>
                        {picked?.id === d.id ? "選択中" : "選ぶ"}
                      </button>
                    </td>
                  </tr>
                ))}
                {candidates.length === 0 && (
                  <tr><td className="muted" style={{ textAlign: "center", padding: 16 }}>該当なし（在籍者のみ表示。退職者・退職予定の方は出ません）</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {candidates.length > 40 && <div className="tiny muted">{candidates.length} 名中 40 名を表示。検索で絞ってください。</div>}
        </Section>

        <Section title="契約終了日">
          <Row>
            <Field label="契約終了日" required>
              <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </Field>
          </Row>
          <div className="tiny muted">
            {future
              ? "未来の日付です。終了日までは支払対象のまま（退職予定）になり、当日を過ぎたらマスター編集の「退職を完了」で退職に切り替えます。"
              : "今日以前の日付なので、送信と同時に「退職」になります。当月分（契約終了日を含む月）の支払通知書は通常どおり作成され、翌月以降の対象から外れます。"}
          </div>
        </Section>

        {!confirming ? (
          <div className="row gap-8">
            <button className="btn primary" disabled={disabled || !picked || !date} onClick={() => setConfirming(true)}>
              <Ic.check size={14} /> 内容を確認する
            </button>
          </div>
        ) : picked ? (
          <div className="card" style={{ padding: 14, background: "var(--surface-2)" }}>
            <div className="card-title" style={{ marginBottom: 8 }}>この内容で契約終了日を書き込みます</div>
            <div className="tiny col gap-2">
              <div>対象: <b>{picked.name}</b>（{picked.id}／{picked.org}／{picked.branch || "—"}）</div>
              <div>契約終了日: <b>{date}</b>{future ? "（退職予定）" : "（退職）"}</div>
            </div>
            <div className="row gap-8" style={{ marginTop: 12 }}>
              <button className="btn primary" disabled={busy} onClick={submit}>
                {busy ? "シートに書き込み中..." : "送信して契約終了日を設定する"}
              </button>
              <button className="btn ghost" disabled={busy} onClick={() => setConfirming(false)}>
                戻る
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ===== 小部品（MasterEdit と同じ見た目） =====

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="col gap-8">
      <div className="tiny muted" style={{ fontWeight: 600, letterSpacing: "0.02em" }}>{title}</div>
      <div className="col gap-8">{children}</div>
    </div>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <div className="row gap-12" style={{ flexWrap: "wrap" }}>
      {children}
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="col gap-4 driver-form-field" style={{ flex: "1 1 200px", minWidth: 180 }}>
      <label className="tiny muted">
        {label}
        {required && <span style={{ color: "#e64646", marginLeft: 4 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function Check({ label, checked, onChange, hint }: { label: string; checked: boolean; onChange: (v: boolean) => void; hint?: string }) {
  return (
    <div className="col gap-4" style={{ flex: "1 1 240px", minWidth: 200 }}>
      <label className="row gap-8" style={{ alignItems: "center", height: 36, cursor: "pointer" }}>
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span>{label}</span>
      </label>
      {hint && <div className="tiny muted">{hint}</div>}
    </div>
  );
}
